import { validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english";
import { isInWordlist } from "./wordlist";
import type { ValidityLevel } from "./types";

const VALID_WORD_COUNTS = [12, 15, 18, 21, 24];

export interface StructuralCheck {
  ok: boolean;
  wordCount: number;
  unknownWords: string[]; // words present but not in the BIP-39 wordlist
  reason?: string;
}

/** Checks shape only: word count and wordlist membership. Does not touch the checksum. */
export function checkStructure(mnemonic: string): StructuralCheck {
  const words = mnemonic.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const unknownWords = words.filter((w) => !isInWordlist(w));

  if (!VALID_WORD_COUNTS.includes(words.length)) {
    return {
      ok: false,
      wordCount: words.length,
      unknownWords,
      reason: `BIP-39 mnemonics have 12, 15, 18, 21, or 24 words; this has ${words.length}.`,
    };
  }
  if (unknownWords.length > 0) {
    return {
      ok: false,
      wordCount: words.length,
      unknownWords,
      reason: `Word(s) not in the BIP-39 English wordlist: ${unknownWords.join(", ")}`,
    };
  }
  return { ok: true, wordCount: words.length, unknownWords: [] };
}

/**
 * Full validity classification for a complete (no blanks) mnemonic candidate.
 * This intentionally stops at "checksum-valid-unverified" — this module has
 * no notion of addresses, so it can never claim "address-verified" itself.
 * That claim can only come from btc-adapter's verify step.
 */
export function classifyMnemonic(mnemonic: string): ValidityLevel {
  const structural = checkStructure(mnemonic);
  if (!structural.ok) return "structurally-invalid";
  const normalized = mnemonic.trim().toLowerCase().split(/\s+/).join(" ");
  return validateMnemonic(normalized, wordlist)
    ? "checksum-valid-unverified"
    : "checksum-invalid";
}
